// frontend/src/components/columns/ChatColumn.tsx
// Right column — only contains the AI Chat interface.
import ChatInterface from '../Chatbot/ChatInterface';
import { useMobile } from '../../contexts/MobileContext';
import type { AcademicPlan } from '../../types';
import type { SuggestionsPayload } from '../../hooks/academicPlan/usePlanCore';

export interface ChatColumnProps {
  academicPlan?: (AcademicPlan & { id?: number | null }) | null;
  /**
   * The plan the chat thread belongs to. Passed explicitly rather than read off
   * `academicPlan.id`, which is the empty-default `0` until the detail fetch
   * lands — the chat would adopt `0`, then flip to the real id, and that flip is
   * what used to destroy the stored thread.
   */
  academicPlanId?: number | null;
  userId?: string;
  /**
   * Accepted but not forwarded. ChatInterface never consumed the
   * `updateDebugInfo` prop this used to feed, so the whole chain — App's
   * `chatDebugInfo` state through to the audit column that displays it — has
   * been inert. Kept here so App's call site still typechecks; unpicking the
   * chain reaches into DegreeAuditColumn and belongs in its own change.
   */
  updateChatDebugInfo?: (info: unknown) => void;
  onExecuteCommand?: (commandString: string) => Promise<unknown> | void;
  onPlanUpdate?: () => Promise<void>;
  onClearSuggestionsLocally?: () => void;
  onMergeSuggestionsLocally?: (payload: SuggestionsPayload) => void;
}

/**
 * Right-column AI chat. Owns the chat interface; forwards plan-mutation
 * callbacks so that chat-driven actions update the planner state.
 */
export function ChatColumn({
  academicPlan,
  academicPlanId,
  userId,
  onExecuteCommand,
  onPlanUpdate,
  onClearSuggestionsLocally,
  onMergeSuggestionsLocally,
}: ChatColumnProps = {}) {
  const { showTabBar } = useMobile();

  return (
    <div
      className="relative chat-column h-full flex flex-col px-2 pr-2 lg:pr-4"
      style={{ overflowX: 'hidden' }}
    >
      <div className={`flex-1 min-h-0 ${showTabBar ? 'pb-20' : 'pb-4 md:pb-6'}`}>
        <ChatInterface
          userId={userId}
          academicPlanId={academicPlanId}
          academicPlan={academicPlan}
          onExecuteCommand={onExecuteCommand}
          onPlanUpdate={onPlanUpdate}
          onClearSuggestionsLocally={onClearSuggestionsLocally}
          onMergeSuggestionsLocally={onMergeSuggestionsLocally}
        />
      </div>
    </div>
  );
}
